import type { UserRole } from '../types/user.js';

/**
 * Capability model (Group Phase 1, Brief 2c) — the fixed set of permission
 * "switches" and the six role presets that map to default capability sets.
 *
 * This is pure policy data plus a pure resolver: it holds no secrets and is
 * imported by the API (for server-side enforcement) and the web app (for hiding
 * UI). THE SERVER IS THE REAL GATE — the web only uses this to decide what to
 * show. Effective capabilities = the role's defaults, then per-user overrides
 * applied on top. super_admin is fixed: it always has every capability and can
 * never be reduced by an override (the resolver short-circuits it).
 */
export const CAPABILITIES = [
  'view_pricing',
  'edit_pricing',
  'view_commission',
  'edit_commission',
  'manage_users',
  'view_all_branches',
  'manage_catalogue',
  'create_quotes',
  'send_documents',
] as const;

export type Capability = (typeof CAPABILITIES)[number];

/** Every capability — the full set super_admin always holds. */
export const ALL_CAPABILITIES: readonly Capability[] = CAPABILITIES;

/**
 * Default capabilities per role. super_admin is the full set; the resolver also
 * hard-guarantees that independently, so this map can never weaken it.
 *
 * Grid (Brief 2c; edit_commission added in Brief 6a):
 *                    super_admin admin owner consultant accounts offshore
 *   view_pricing          ✅      ✅    ✅       ✅         ✅        —
 *   edit_pricing          ✅      ✅    —        —          —         —
 *   view_commission       ✅      ✅    ✅       —          ✅        —
 *   edit_commission       ✅      ✅    —        —          —         —
 *   manage_users          ✅      —     —        —          —         —
 *   view_all_branches     ✅      ✅    ✅       —          ✅        —
 *   manage_catalogue      ✅      ✅    —        —          —         —
 *   create_quotes         ✅      ✅    —        ✅         —         ✅
 *   send_documents        ✅      ✅    —        ✅         —         ✅
 */
export const ROLE_DEFAULT_CAPABILITIES: Record<UserRole, readonly Capability[]> = {
  super_admin: [...CAPABILITIES],
  admin: [
    'view_pricing',
    'edit_pricing',
    'view_commission',
    'edit_commission',
    'view_all_branches',
    'manage_catalogue',
    'create_quotes',
    'send_documents',
  ],
  owner: ['view_pricing', 'view_commission', 'view_all_branches'],
  consultant: ['view_pricing', 'create_quotes', 'send_documents'],
  accounts: ['view_pricing', 'view_commission', 'view_all_branches'],
  offshore: ['create_quotes', 'send_documents'],
};

/** A single per-user override: force one capability ON (granted) or OFF. */
export type CapabilityOverride = {
  capability: Capability;
  granted: boolean;
};

/**
 * Resolve a user's EFFECTIVE capabilities: role defaults, then overrides applied
 * on top. super_admin always resolves to the full set — overrides are ignored for
 * them, so they can never be reduced. This is the single source of truth used by
 * the server-side `requireCapability` guard and by response shapers.
 */
export function effectiveCapabilities(
  role: UserRole,
  overrides: readonly CapabilityOverride[] = [],
): Set<Capability> {
  if (role === 'super_admin') return new Set<Capability>(CAPABILITIES);
  const caps = new Set<Capability>(ROLE_DEFAULT_CAPABILITIES[role]);
  for (const ov of overrides) {
    if (ov.granted) caps.add(ov.capability);
    else caps.delete(ov.capability);
  }
  return caps;
}

/** Type guard: is an arbitrary string one of the known capabilities? */
export function isCapability(value: string): value is Capability {
  return (CAPABILITIES as readonly string[]).includes(value);
}
